/**
 * Floating quick chat / voice composers on the canvas: flow session creation, `chatSession`
 * node placement, and selection state shared with `ChatSessionNode`. Also builds the memoized
 * `CanvasChatSessionProvider` value (`chatSessionContextValue`) so `nodeTypes` stay stable.
 */

import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import { useUserID } from "@/hooks/userUserID";
import { useFileRecordTextSelectionReferencedEntity } from "@/panes/file-record-detail/useFileRecordTextSelectionReferencedEntity";
import { mergeCanvasReferencedEntities } from "@/utils/mergeReferencedEntities";
import {
  FLOW_SESSION_MODES,
  type ReferencedEntity,
} from "@cohesive/api-client";
import { useReactFlow } from "@xyflow/react";
import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import type { CanvasChatSessionContextValue } from "../context/CanvasChatSessionContext";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import { useCanvasSelectionMirrorGetter } from "../context/CanvasSelectionMirrorContext";
import { convertNodesToReferencedEntities } from "../utils/convertNodesToReferencedEntities";
import { snapFlowPosition } from "../utils/quickAddCursor";
import { useCanvasReasoningEffort } from "./useCanvasReasoningEffort";

export type QuickChatMode = "chat" | "voice";

export type QuickChatSubmitResult = {
  nodeId: string | null;
  /** True once the flow message was posted (selections should leave the cache). */
  messageSent: boolean;
};

export interface QuickChatComposingState {
  mode: QuickChatMode;
  screenPosition: { x: number; y: number };
  flowPosition: { x: number; y: number };
  /** Pre-filled text — set when voice transcription completes and mode switches to "chat" */
  initialContent?: string;
}

export function useQuickChat() {
  const { screenToFlowPosition } = useReactFlow();
  const { canvasId } = useCanvasRoute();
  const { addNode, updateNodeData } = useCanvas();
  const createFlowSession = useCreateFlowSession();
  const postMessage = usePostFlowMessage();
  const getSelectedNodes = useCanvasSelectionMirrorGetter();
  const currentUserId = useUserID();
  const [reasoningEffort] = useCanvasReasoningEffort(canvasId);
  const { textSelectionEntities, clearSelectionsFromMessage } =
    useFileRecordTextSelectionReferencedEntity(canvasId ?? "");

  const [composingState, setComposingState] =
    useState<QuickChatComposingState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Expanded/collapsed state is persisted on each chat session node's `data`
  // (Loro/Liveblocks) so it survives reloads. Multiple quick chats can stay
  // open at once; they only close via the panel close button or by clicking
  // their own icon/node — never on a pane click or when another opens.
  const setChatExpanded = useCallback(
    (id: string, expanded: boolean) => {
      updateNodeData(id, { expanded });
    },
    [updateNodeData],
  );

  const openChat = useCallback(
    (id: string) => setChatExpanded(id, true),
    [setChatExpanded],
  );

  const handleStartQuickChat = useCallback(
    (screenPos: { x: number; y: number }, initialContent?: string) => {
      const flowPos = screenToFlowPosition(screenPos);
      setComposingState({
        mode: "chat",
        screenPosition: screenPos,
        flowPosition: flowPos,
        initialContent,
      });
    },
    [screenToFlowPosition],
  );

  const handleStartQuickVoice = useCallback(
    (screenPos: { x: number; y: number }) => {
      const flowPos = screenToFlowPosition(screenPos);
      setComposingState({
        mode: "voice",
        screenPosition: screenPos,
        flowPosition: flowPos,
      });
    },
    [screenToFlowPosition],
  );

  const handleSubmitQuickChat = useCallback(
    async (
      content: string,
      referencedEntities: ReferencedEntity[],
      /** Current screen position of the composer at submit time (after any dragging) */
      composerScreenPos?: { x: number; y: number },
      /** Reasoning effort selected in the composer at send time. Falls back to canvas-persisted value. */
      agentReasoningEffortOverride?: string,
    ): Promise<QuickChatSubmitResult> => {
      if (!canvasId || !composingState) {
        return { nodeId: null, messageSent: false };
      }

      // Node is placed at the flow position corresponding to where the composer was.
      // The ChatPanel renders to the right of the icon automatically via ViewportPortal.
      const screenPos = composerScreenPos ?? composingState.screenPosition;
      // screenToFlowPosition is raw (no RF snapToGrid) — put the session
      // node on the lattice, matching the snapped placement ghost.
      const iconFlowPos = snapFlowPosition(screenToFlowPosition(screenPos));

      setIsSubmitting(true);
      let messageSent = false;
      try {
        const newSession = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_CHAT,
          class: "canvas",
          canvas_id: canvasId,
        });

        await postMessage.mutateAsync({
          flowSessionId: newSession.id,
          id: uuidv4(),
          content,
          referenced_entities: referencedEntities,
          reasoning_effort: agentReasoningEffortOverride ?? reasoningEffort,
        });
        messageSent = true;

        const nodeId = `chat-session-${uuidv4()}`;
        addNode({
          id: nodeId,
          type: "chatSession",
          position: iconFlowPos,
          data: {
            flowSessionId: newSession.id,
            status: "active",
            firstMessage: content.slice(0, 80),
            userId: currentUserId,
            expanded: true,
          },
        });

        setComposingState(null);

        return { nodeId, messageSent: true };
      } catch (error) {
        console.error("[useQuickChat] Failed to submit:", error);
        return { nodeId: null, messageSent };
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      canvasId,
      composingState,
      screenToFlowPosition,
      createFlowSession,
      postMessage,
      addNode,
      currentUserId,
      reasoningEffort,
    ],
  );

  const handleCancelComposing = useCallback(() => {
    setComposingState(null);
  }, []);

  const handleTranscriptionReady = useCallback(
    (text: string, currentPos?: { x: number; y: number }) => {
      if (!composingState) return;
      setComposingState({
        ...composingState,
        mode: "chat",
        initialContent: text,
        screenPosition: currentPos ?? composingState.screenPosition,
      });
    },
    [composingState],
  );

  /** Skips the text composer entirely — sends the transcribed text immediately.
   *  Reads the current canvas node selection from the query cache and merges in
   *  cached file-record text excerpts so it behaves identically to the
   *  stop-then-review path through QuickChatComposer. */
  const handleTranscriptionAndSend = useCallback(
    async (text: string, currentPos?: { x: number; y: number }) => {
      if (!composingState) return;
      const pos = currentPos ?? composingState.screenPosition;
      const selectedNodes = getSelectedNodes();
      const referencedEntities = mergeCanvasReferencedEntities(
        convertNodesToReferencedEntities(selectedNodes),
        textSelectionEntities,
        new Set(),
      );
      setComposingState(null);
      const { messageSent } = await handleSubmitQuickChat(
        text,
        referencedEntities,
        pos,
      );
      if (messageSent) {
        clearSelectionsFromMessage(referencedEntities);
      }
    },
    [
      composingState,
      handleSubmitQuickChat,
      getSelectedNodes,
      textSelectionEntities,
      clearSelectionsFromMessage,
    ],
  );

  const chatSessionContextValue = useMemo<CanvasChatSessionContextValue>(
    () => ({
      setChatExpanded,
      canvasId: canvasId ?? "",
    }),
    [setChatExpanded, canvasId],
  );

  return {
    composingState,
    openChat,
    setChatExpanded,
    chatSessionContextValue,
    isSubmitting,
    handleStartQuickChat,
    handleStartQuickVoice,
    handleSubmitQuickChat,
    handleTranscriptionReady,
    handleTranscriptionAndSend,
    handleCancelComposing,
  };
}
